import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  ListGroup,
  Button,
  Card,
  Form,
  Accordion,
} from "react-bootstrap";
import { FaShoppingCart, FaStickyNote, FaTrash } from "react-icons/fa";
import Message from "../components/Message";
import {
  addToCart,
  removeFromCart,
  clearCartItems,
} from "../slices/cartSlice";

const CartScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);

  const nav = (path) => navigate(`/login?redirect=/${path}`);

  // ⭐ Dark Mode
  const [darkMode, setDarkMode] = useState(false);

  // ⭐ PRODUCT FILTER (LEFT PANEL)
  const [productFilter, setProductFilter] = useState("");

  const filteredCartItems = useMemo(() => {
    if (!productFilter.trim()) return cartItems;

    return cartItems.filter((item) =>
      item.name.toLowerCase().includes(productFilter.toLowerCase()) ||
      String(item.price).includes(productFilter) ||
      String(item.qty).includes(productFilter) ||
      String(item._id).includes(productFilter)
    );
  }, [productFilter, cartItems]);

  // ⭐ PAGE FILTER (RIGHT PANEL — unchanged)
  const [search, setSearch] = useState("");

  const categories = useMemo(
    () => ({
      "Sales & Daily Stock": {
        color: "#007bff",
        icon: <FaShoppingCart className="me-2" />,
        items: {
          salesform: { label: "Proceed To SalesForm", hidden: false },
          dailystocksform: { label: "Proceed To DailyStockForm", hidden: false },

          supplysform: { label: "Proceed To SupplyForm", hidden: true },
          transfersform: { label: "Proceed To TransferForm", hidden: true },
          stocksform: { label: "Proceed To Stock", hidden: true },
          openingstocksform: { label: "Proceed To Op-Stock", hidden: true },
        },
      },

      Billing: {
        color: "#6f42c1",
        icon: <FaStickyNote className="me-2" />,
        items: {
          insurancesform: { label: "InsuForm", hidden: false },
        },
      },

      "Income & Expense": {
        color: "#ffc107",
        icon: <FaStickyNote className="me-2" />,
        items: {
          expenselist: { label: "Credits / Debits", hidden: true },
        },
      },

      "Products & Inventory": {
        color: "#28a745",
        icon: <FaStickyNote className="me-2" />,
        items: {
          productlist: { label: "Itemlist", hidden: true },
        },
      },

      "Cash & Bank": {
        color: "#17a2b8",
        icon: <FaStickyNote className="me-2" />,
        items: {
          cashlist: { label: "Cash Collected", hidden: true },
        },
      },

      People: {
        color: "#fd7e14",
        icon: <FaStickyNote className="me-2" />,
        items: {
          customeruserlist: { label: "Customerlist", hidden: true },
          supplierlist: { label: "Supplierlist", hidden: true },
        },
      },

      Misc: {
        color: "#6c757d",
        icon: <FaStickyNote className="me-2" />,
        items: {
          services: { label: "Services", hidden: true },
          contact: { label: "Contact Us", hidden: true },
        },
      },
    }),
    []
  );

  const filteredCategories = useMemo(() => {
    const result = {};

    Object.entries(categories).forEach(([catName, catData]) => {
      const visibleItems = Object.entries(catData.items).filter(
        ([, item]) => !item.hidden
      );

      if (visibleItems.length === 0) return;

      const matched = visibleItems.filter(([path, item]) =>
        item.label.toLowerCase().includes(search.toLowerCase())
      );

      if (matched.length > 0) {
        result[catName] = {
          ...catData,
          items: matched.reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {}),
        };
      }
    });

    return result;
  }, [search, categories]);

  // ⭐ Subtotal memo
  const subtotal = useMemo(
    () => cartItems.reduce((acc, item) => acc + item.qty * item.price, 0),
    [cartItems]
  );

  // ⭐ Total quantity memo
  const totalQty = useMemo(
    () => cartItems.reduce((acc, item) => acc + item.qty, 0),
    [cartItems]
  );

  // ⭐ FULL RETURN BLOCK
  return (
    <Row className={darkMode ? "bg-dark text-light p-3" : "p-3"}>
      {/* LEFT PANEL */}
      <Col md={8}>
        <h1 className="mb-4">
          {darkMode ? "🛒 Shopping Cart (Dark Mode)" : "Shopping Cart"}
        </h1>

        {/* ⭐ PRODUCT FILTER (LEFT PANEL) */}
        <Form.Group className="mb-3">
          <Form.Label>Search Products</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search product name, price, qty, ID..."
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            style={{
              backgroundColor: darkMode ? "#333" : "white",
              color: darkMode ? "white" : "black",
            }}
          />
        </Form.Group>

        {filteredCartItems.length === 0 ? (
          <Message variant={darkMode ? "dark" : "light"}>
            No products match your search.
          </Message>
        ) : (
          <ListGroup variant={darkMode ? "dark" : "flush"}>
            {filteredCartItems.map((item) => (
              <ListGroup.Item
                key={item._id}
                className={darkMode ? "bg-secondary text-light" : ""}
              >
                <Row className="align-items-center">
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Thumbnail"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    )}
                  </Col>

                  <Col md={3}>
                    <Link
                      to={`/product/${item._id}`}
                      className={darkMode ? "text-warning" : ""}
                    >
                      {item.name}
                    </Link>
                  </Col>

                  <Col md={2}>D{item.price}</Col>

                  <Col md={2}>
                    <Form.Control
                      type="number"
                      min="1"
                      max={item.countInStock}
                      value={item.qty}
                      onChange={(e) => {
                        const newQty = Number(e.target.value);
                        if (newQty >= 1 && newQty <= item.countInStock) {
                          dispatch(addToCart({ ...item, qty: newQty }));
                        }
                      }}
                      style={{
                        width: "70px",
                        textAlign: "center",
                        backgroundColor: darkMode ? "#333" : "white",
                        color: darkMode ? "white" : "black",
                      }}
                    />
                  </Col>

                  <Col md={2}>D{(item.price * item.qty).toFixed(2)}</Col>

                  <Col md={1}>
                    <Button
                      variant={darkMode ? "outline-light" : "light"}
                      onClick={() => dispatch(removeFromCart(item._id))}
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* RIGHT PANEL */}
      <Col md={4}>
        <Card className={darkMode ? "bg-secondary text-light" : ""}>
          <ListGroup variant="flush">
            <ListGroup.Item className={darkMode ? "bg-secondary text-light" : ""}>
              <h2>Subtotal ({totalQty}) items</h2>
              D{subtotal.toFixed(2)}
            </ListGroup.Item>

            {/* PAGE FILTER (RIGHT PANEL — unchanged) */}
            <ListGroup.Item className={darkMode ? "bg-secondary text-light" : ""}>
              <Form.Control
                type="text"
                placeholder="Search actions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  backgroundColor: darkMode ? "#333" : "white",
                  color: darkMode ? "white" : "black",
                }}
              />
            </ListGroup.Item>

            {/* DARK MODE */}
            <ListGroup.Item className={darkMode ? "bg-secondary text-light" : ""}>
              <Form.Check
                type="switch"
                id="dark-mode-switch"
                label="Dark Mode"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
              />
            </ListGroup.Item>

            {/* COLLAPSIBLE SECTIONS */}
            <ListGroup.Item className={darkMode ? "bg-secondary text-light" : ""}>
              <Accordion alwaysOpen>
                {Object.entries(filteredCategories).map(
                  ([catName, { color, icon, items }], idx) => (
                    <Accordion.Item
                      eventKey={idx.toString()}
                      key={catName}
                      className={darkMode ? "bg-dark text-light" : ""}
                    >
                      <Accordion.Header>
                        <span style={{ color }}>{icon}{catName}</span>
                      </Accordion.Header>

                      <Accordion.Body className={darkMode ? "bg-secondary text-light" : ""}>
                        <Row>
                          {Object.entries(items).map(([path, item]) => (
                            <Col md={6} className="mb-2" key={path}>
                              <Button
                                className="w-100"
                                style={{
                                  backgroundColor: color,
                                  borderColor: color,
                                }}
                                disabled={cartItems.length === 0}
                                onClick={() => nav(path)}
                              >
                                {item.label}
                              </Button>
                            </Col>
                          ))}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  )
                )}
              </Accordion>
            </ListGroup.Item>

            {/* CLEAR CART */}
            <ListGroup.Item className={darkMode ? "bg-secondary text-light" : ""}>
              <Button
                className="w-100"
                disabled={cartItems.length === 0}
                onClick={() => dispatch(clearCartItems())}
              >
                Clear Cart
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default CartScreen;
